// Blur Safety Check — analyserer om sladding i et bilde er trygg mot AI-reversering.
// Alt lokalt i nettleseren. Ingen opplasting.

/**
 * Analyser en 3×3 piksel-patch og klassifiser den:
 *  - 'destructive': alle 9 piksler identiske (Veyl-stil destruktiv nedskalering)
 *  - 'soft':        lav varians, glatte overganger (Gaussian blur — reverserbar)
 *  - 'mosaic':      middels varians (typisk Snapchat/Instagram-mosaikk)
 *  - 'sharp':       normal varians (ingen sladding)
 */
function classifyPatch(pixels) {
  // pixels = 9 elementer, hver [r, g, b]
  const first = pixels[0];
  let allIdentical = true;
  for (let i = 1; i < pixels.length; i++) {
    if (pixels[i][0] !== first[0] || pixels[i][1] !== first[1] || pixels[i][2] !== first[2]) {
      allIdentical = false;
      break;
    }
  }
  if (allIdentical) return "destructive";

  // Beregn varians per kanal
  const means = [0, 0, 0];
  for (const p of pixels) { means[0] += p[0]; means[1] += p[1]; means[2] += p[2]; }
  means[0] /= 9; means[1] /= 9; means[2] /= 9;

  let variance = 0;
  for (const p of pixels) {
    variance += (p[0] - means[0]) ** 2 + (p[1] - means[1]) ** 2 + (p[2] - means[2]) ** 2;
  }
  variance /= 27; // 9 piksler × 3 kanaler

  if (variance < 8) return "soft";      // Gaussian blur — veldig glatt
  if (variance < 40) return "mosaic";   // Typisk resampling
  return "sharp";
}

/**
 * Analyser en region av bildet og gi en total-vurdering.
 */
export function analyzeRegion(imageData, x, y, w, h, samples = 800) {
  const counts = { destructive: 0, soft: 0, mosaic: 0, sharp: 0 };
  const totalW = imageData.width;
  const data = imageData.data;

  const attempts = Math.min(samples, Math.max(50, w * h / 3));
  for (let i = 0; i < attempts; i++) {
    const px = x + Math.floor(Math.random() * (w - 3));
    const py = y + Math.floor(Math.random() * (h - 3));
    const pixels = [];
    for (let dy = 0; dy < 3; dy++) {
      for (let dx = 0; dx < 3; dx++) {
        const idx = ((py + dy) * totalW + (px + dx)) * 4;
        pixels.push([data[idx], data[idx + 1], data[idx + 2]]);
      }
    }
    counts[classifyPatch(pixels)]++;
  }

  const total = attempts;
  const ratios = {
    destructive: counts.destructive / total,
    soft: counts.soft / total,
    mosaic: counts.mosaic / total,
    sharp: counts.sharp / total,
  };

  // Konklusjon-logikk
  let verdict, level, message;
  if (ratios.destructive > 0.35) {
    verdict = "safe";
    level = 3; // Trygg
    message = "Denne sladdingen kan ikke reverseres av AI. Piksel-informasjonen er permanent ødelagt.";
  } else if (ratios.mosaic > 0.4 && ratios.soft < 0.3) {
    verdict = "partial";
    level = 2; // Delvis trygg
    message = "Sladdingen er en mosaikk. Kan motstå enkle verktøy, men avanserte AI-modeller kan gjenskape mye.";
  } else if (ratios.soft > 0.3 || (ratios.soft + ratios.mosaic > 0.5 && ratios.destructive < 0.1)) {
    verdict = "unsafe";
    level = 1; // Usikker
    message = "Dette er Gaussian blur — moderne AI-verktøy kan reversere den og hente frem ansiktet/detaljene.";
  } else if (ratios.sharp > 0.8) {
    verdict = "none";
    level = 0; // Ingen sladding funnet
    message = "Ingen tydelig sladding funnet i det markerte området.";
  } else {
    verdict = "mixed";
    level = 1;
    message = "Blandet resultat. Deler av sladdingen kan reverseres. Vurder å behandle bildet på nytt med et destruktivt verktøy.";
  }

  return { verdict, level, message, ratios, samples: total };
}

/**
 * Analyser HELE bildet og bygg en heatmap (canvas) som viser hvilke områder som
 * er trygge (grønn), delvis (gul), utrygge (rød), normale (transparente).
 * Returnerer også overall verdict.
 */
export async function analyzeImageFull(sourceCanvas, opts = {}) {
  // Finere celle-oppløsning + tettere sampling så små sladdede områder ikke
  // sniker seg forbi.
  const { gridSize = 10 } = opts;
  const w = sourceCanvas.width;
  const h = sourceCanvas.height;
  const ctx = sourceCanvas.getContext("2d", { willReadFrequently: true });
  const imgData = ctx.getImageData(0, 0, w, h);

  const heatmap = document.createElement("canvas");
  heatmap.width = w;
  heatmap.height = h;
  const hctx = heatmap.getContext("2d");

  const cellsX = Math.ceil(w / gridSize);
  const cellsY = Math.ceil(h / gridSize);

  // Tell absolutte antall sladde-celler (ikke bare gjennomsnitt) — så små
  // sladdede områder fortsatt oppdages selv i store bilder.
  let cellCounts = { destructive: 0, soft: 0, mosaic: 0, sharp: 0, none: 0 };

  for (let cy = 0; cy < cellsY; cy++) {
    for (let cx = 0; cx < cellsX; cx++) {
      const rx = cx * gridSize;
      const ry = cy * gridSize;
      const rw = Math.min(gridSize, w - rx);
      const rh = Math.min(gridSize, h - ry);
      if (rw < 3 || rh < 3) continue;

      // Eksaustiv sampling — hver mulig 3x3 patch i cellen
      const local = analyzeRegion(imgData, rx, ry, rw, rh, (rw - 2) * (rh - 2));

      cellCounts[local.verdict] = (cellCounts[local.verdict] || 0) + 1;

      let color = null;
      if (local.verdict === "safe") color = "rgba(34, 197, 94, 0.55)";
      else if (local.verdict === "partial") color = "rgba(234, 179, 8, 0.55)";
      else if (local.verdict === "unsafe") color = "rgba(239, 68, 68, 0.55)";
      if (color) {
        hctx.fillStyle = color;
        hctx.fillRect(rx, ry, rw, rh);
      }
    }
  }

  const totalCells = cellsX * cellsY;
  const safeCells = cellCounts.safe || 0;
  const partialCells = cellCounts.partial || 0;
  const unsafeCells = cellCounts.unsafe || 0;
  const noneCells = cellCounts.none || 0;

  const ratios = {
    destructive: safeCells / totalCells,
    mosaic: partialCells / totalCells,
    soft: unsafeCells / totalCells,
    sharp: noneCells / totalCells,
  };

  // Overall verdict — basert på absolutt antall sladdede celler,
  // ikke bare gjennomsnitt. Selv 10 celler av 10 000 teller.
  const totalSladdCells = safeCells + partialCells + unsafeCells;
  const MIN_CELLS_TO_FLAG = 8; // minst 8 sladdede celler for å konkludere

  let verdict, level, message;
  if (totalSladdCells < MIN_CELLS_TO_FLAG) {
    verdict = "none";
    level = 0;
    message = "Ingen tydelig sladding funnet i bildet. Enten er det ikke sladdet, eller området er for lite til å oppdages sikkert.";
  } else if (unsafeCells >= MIN_CELLS_TO_FLAG && safeCells < unsafeCells * 0.3) {
    verdict = "unsafe";
    level = 1;
    message = `Fant ${unsafeCells} områder med Gaussian blur som kan reverseres av AI. Bruk et destruktivt verktøy (som Veyl) for å beskytte bildet.`;
  } else if (safeCells >= MIN_CELLS_TO_FLAG && unsafeCells < safeCells * 0.3) {
    verdict = "safe";
    level = 3;
    message = `Fant ${safeCells} områder med destruktiv sladding. Piksel-informasjonen er permanent ødelagt — kan ikke reverseres av AI.`;
  } else if (safeCells >= MIN_CELLS_TO_FLAG && unsafeCells >= MIN_CELLS_TO_FLAG) {
    verdict = "mixed";
    level = 2;
    message = "Deler av sladdingen er trygg, deler kan reverseres. Behandle de røde områdene på nytt.";
  } else if (partialCells >= MIN_CELLS_TO_FLAG) {
    verdict = "partial";
    level = 2;
    message = "Sladdingen ser ut som mosaikk. Delvis motstandsdyktig, men ikke garantert trygg.";
  } else {
    verdict = "none";
    level = 0;
    message = "Ingen tydelig sladding funnet.";
  }

  return { verdict, level, message, ratios, heatmap, cellCounts };
}
